import {RulesLogic, Spot, TriggerRule, Zone} from "../../components/types";
export {compareVersions} from "../../components/common/compareVersions";

// --- Types ---

export type CueListEntry =
    | { kind: "spot"; spot: Spot; index: number }
    | { kind: "ambient"; zone: Zone };

export type CueListGroupSection = {
    entries: CueListEntry[];
};

// --- Row key helpers ---

export const getSpotRowKey = (spotId: string) => `spot:${spotId}`;
export const getAmbientRowKey = (zoneId: string) => `ambient:${zoneId}`;

export const getCueLabelFromFileName = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf(".");
    const baseName = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
    const trimmed = baseName.trim();
    return trimmed.length > 0 ? trimmed : fileName;
};

// --- Rule event type helpers ---

export const isNegativeSpotEventType = (eventType?: string) =>
    eventType === "SPOT_NOT_TRIGGERED" || eventType === "SPOT_NOT_FINISHED";

export const isNegativeEventType = (eventType?: string) =>
    isNegativeSpotEventType(eventType) || eventType === "AMBIENT_NOT_TRIGGERED";

export const hasOnlyNegativeRules = (rules?: Array<{type: string; eventType: string; spotKey?: string; zoneKey?: string}>) => {
    if (!rules || rules.length === 0) {
        return false;
    }
    return rules.every((rule) => isNegativeEventType(rule.eventType));
};

// --- Ambient relation detection ---

export const hasAmbientRelationRule = (zone: Zone) => {
    const rules = zone.triggerRules?.rules || [];
    return rules.some((rule) => {
        if (rule.type === "Spot" && rule.spotKey) {
            return rule.eventType === "SPOT_FINISHED"
                || rule.eventType === "SPOT_TRIGGERED"
                || rule.eventType === "SPOT_FINISHED_FOR_MS"
                || rule.eventType === "SPOT_NOT_TRIGGERED"
                || rule.eventType === "SPOT_NOT_FINISHED";
        }
        if (rule.type === "Ambient" && rule.zoneKey) {
            return rule.eventType === "AMBIENT_TRIGGERED"
                || rule.eventType === "AMBIENT_NOT_TRIGGERED"
                || rule.eventType === "AMBIENT_TIME_SINCE_TRIGGERED";
        }
        return false;
    });
};

export const hasEntryRules = (entry: CueListEntry) => {
    if (entry.kind === "spot") {
        const rules = entry.spot.triggerRules?.rules || [];
        if (rules.length === 0) {
            return false;
        }
        return !hasOnlyNegativeRules(rules);
    }
    const rules = entry.zone.triggerRules?.rules || [];
    if (rules.length === 0) {
        return false;
    }
    return hasAmbientRelationRule(entry.zone);
};

// --- Managed cue rule detection ---

export const MANAGED_CUE_PREFIX = "managed_cue_";

export const isManagedCueRule = (rule: {id?: string; type?: string; eventType?: string}) => {
    if (rule.id?.startsWith(MANAGED_CUE_PREFIX)) {
        return true;
    }
    return !!(rule.id?.startsWith("ambient_cue_start_") || rule.id?.startsWith("ambient_cue_end_"));

};

// --- Ambient anchor detection ---

export const getAmbientAnchorRule = (zone: Zone): {spotKey?: string; position: "top" | "before" | "after"} | undefined => {
    const rules = zone.triggerRules?.rules || [];
    const startsAfterRule = rules.find((rule) => rule.type === "Spot" && rule.eventType === "SPOT_FINISHED" && !!rule.spotKey)
        || rules.find((rule) => rule.type === "Spot" && (rule.eventType === "SPOT_TRIGGERED" || rule.eventType === "SPOT_FINISHED_FOR_MS") && !!rule.spotKey);
    if (startsAfterRule?.spotKey) {
        return {
            spotKey: startsAfterRule.spotKey,
            position: "after"
        };
    }
    if (hasOnlyNegativeRules(rules as Array<{type: string; eventType: string; spotKey?: string; zoneKey?: string}>)) {
        return {
            position: "top"
        };
    }
    const beforeRule = rules.find((rule) => rule.type === "Spot"
        && (rule.eventType === "SPOT_NOT_TRIGGERED" || rule.eventType === "SPOT_NOT_FINISHED")
        && !!rule.spotKey);
    if (beforeRule?.spotKey) {
        return {
            spotKey: beforeRule.spotKey,
            position: "before"
        };
    }
    return undefined;
};

// --- Rule deduplication ---

export const hasEquivalentRule = (rules: TriggerRule[], candidate: TriggerRule): boolean => {
    return rules.some((r) =>
        r.type === candidate.type
        && r.eventType === candidate.eventType
        && (r.spotKey || undefined) === (candidate.spotKey || undefined)
        && (r.zoneKey || undefined) === (candidate.zoneKey || undefined)
    );
};

export const mergeRulesWithoutDuplicates = (preserved: TriggerRule[], managed: TriggerRule[]): TriggerRule[] => {
    const result = [...preserved];
    for (const rule of managed) {
        if (!hasEquivalentRule(result, rule)) {
            result.push(rule);
        }
    }
    return result;
};

// --- Legacy rule builder (< 4.1) ---

export const buildAmbientZonesFromEntries = (entries: CueListEntry[], ambientZones: Zone[]): Zone[] => {
    const ambientById = new Map(ambientZones.map((zone) => [zone.id, zone]));
    const ambientIdsInOrder = entries
        .filter((entry): entry is {kind: "ambient"; zone: Zone} => entry.kind === "ambient")
        .map((entry) => entry.zone.id);
    const lastSpotKey = [...entries]
        .reverse()
        .find((entry): entry is {kind: "spot"; spot: Spot; index: number} => entry.kind === "spot" && !!entry.spot.key)
        ?.spot.key;

    const getPreviousSpotKey = (entryIndex: number): string | undefined => {
        for (let index = entryIndex - 1; index >= 0; index--) {
            const entry = entries[index];
            if (entry.kind === "spot" && entry.spot.key) {
                return entry.spot.key;
            }
        }
        return undefined;
    };

    const ambientStartSpotKey = new Map<string, string | undefined>();
    entries.forEach((entry, index) => {
        if (entry.kind !== "ambient") {
            return;
        }
        ambientStartSpotKey.set(entry.zone.id, getPreviousSpotKey(index));
    });

    const updatedInOrder: Zone[] = [];
    ambientIdsInOrder.forEach((ambientId, ambientIndex) => {
        const zone = ambientById.get(ambientId);
        if (!zone) {
            return;
        }

        const startSpotKey = ambientStartSpotKey.get(ambientId);
        const nextAmbientId = ambientIdsInOrder[ambientIndex + 1];
        const nextAmbientStartSpotKey = nextAmbientId ? ambientStartSpotKey.get(nextAmbientId) : undefined;
        const terminalSpotKey = nextAmbientStartSpotKey || lastSpotKey;

        const preservedRules = (zone.triggerRules?.rules || []).filter((rule) => !isManagedCueRule(rule));
        const managedRules = [];
        if (startSpotKey) {
            managedRules.push({
                id: `ambient_cue_start_${zone.id}`,
                type: "Spot" as const,
                eventType: "SPOT_FINISHED" as const,
                spotKey: startSpotKey
            });
        }
        if (terminalSpotKey && terminalSpotKey !== startSpotKey) {
            managedRules.push({
                id: `ambient_cue_end_${zone.id}`,
                type: "Spot" as const,
                eventType: nextAmbientStartSpotKey ? "SPOT_NOT_TRIGGERED" as const : "SPOT_NOT_FINISHED" as const,
                spotKey: terminalSpotKey
            });
        }

        const nextRules = mergeRulesWithoutDuplicates(preservedRules, managedRules);
        updatedInOrder.push({
            ...zone,
            triggerRules: nextRules.length > 0 ? {
                logic: RulesLogic.AND,
                rules: nextRules
            } : undefined
        });
    });

    const knownIds = new Set(updatedInOrder.map((zone) => zone.id));
    const remaining = ambientZones.filter((zone) => !knownIds.has(zone.id));
    return [...updatedInOrder, ...remaining];
};

// --- Ambient-aware rule builder (>= 4.1) ---

export const buildMergedCueListRules = (
    entries: CueListEntry[],
    ambientZones: Zone[]
): {updatedZones: Zone[]; spotManagedRules: Map<string, TriggerRule[]>} => {
    const ambientById = new Map(ambientZones.map((zone) => [zone.id, zone]));
    const ambientIdsInOrder = entries
        .filter((entry): entry is {kind: "ambient"; zone: Zone} => entry.kind === "ambient")
        .map((entry) => entry.zone.id);

    const ambientMergedIndices = new Map<string, number>();
    entries.forEach((entry, index) => {
        if (entry.kind === "ambient") {
            ambientMergedIndices.set(entry.zone.id, index);
        }
    });

    const getPreviousSpot = (entryIndex: number): Spot | undefined => {
        for (let i = entryIndex - 1; i >= 0; i--) {
            const e = entries[i];
            if (e.kind === "spot") return e.spot;
        }
        return undefined;
    };

    const getPreviousAmbient = (entryIndex: number): Zone | undefined => {
        for (let i = entryIndex - 1; i >= 0; i--) {
            const e = entries[i];
            if (e.kind === "ambient") return e.zone;
        }
        return undefined;
    };

    const isFirstSpotInSection = (entryIndex: number): boolean => {
        for (let i = entryIndex - 1; i >= 0; i--) {
            if (entries[i].kind === "spot") return false;
            if (entries[i].kind === "ambient") return true;
        }
        return false;
    };

    // Build spot managed rules
    const spotManagedRules = new Map<string, TriggerRule[]>();
    entries.forEach((entry, index) => {
        if (entry.kind !== "spot") return;
        const spot = entry.spot;
        const rules: TriggerRule[] = [];

        if (index === 0) {
            // First item in list: no managed rules (keeps area default triggers)
        } else if (isFirstSpotInSection(index)) {
            const prevAmbient = getPreviousAmbient(index);
            if (prevAmbient) {
                rules.push({
                    id: `${MANAGED_CUE_PREFIX}${spot.id}_0`,
                    type: "Ambient",
                    eventType: "AMBIENT_TRIGGERED",
                    zoneKey: prevAmbient.id
                } as TriggerRule);
            }
        } else {
            const prevSpot = getPreviousSpot(index);
            if (prevSpot?.key) {
                rules.push({
                    id: `${MANAGED_CUE_PREFIX}${spot.id}_0`,
                    type: "Spot",
                    eventType: "SPOT_FINISHED",
                    spotKey: prevSpot.key
                } as TriggerRule);
            }
        }
        spotManagedRules.set(spot.id, rules);
    });

    // Build ambient zone rules
    const updatedZones: Zone[] = [];
    ambientIdsInOrder.forEach((ambientId, ambientIndex) => {
        const zone = ambientById.get(ambientId);
        if (!zone) return;

        const mergedIndex = ambientMergedIndices.get(ambientId) ?? -1;
        const preservedRules = (zone.triggerRules?.rules || []).filter((rule) => !isManagedCueRule(rule));
        const managedRules: TriggerRule[] = [];

        const prevSpot = getPreviousSpot(mergedIndex);
        if (prevSpot?.key) {
            managedRules.push({
                id: `${MANAGED_CUE_PREFIX}${zone.id}_0`,
                type: "Spot",
                eventType: "SPOT_FINISHED",
                spotKey: prevSpot.key
            } as TriggerRule);
        }

        const nextAmbientId = ambientIdsInOrder[ambientIndex + 1];
        if (nextAmbientId) {
            managedRules.push({
                id: `${MANAGED_CUE_PREFIX}${zone.id}_1`,
                type: "Ambient",
                eventType: "AMBIENT_NOT_TRIGGERED",
                zoneKey: nextAmbientId
            } as TriggerRule);
        }

        const nextRules = mergeRulesWithoutDuplicates(preservedRules, managedRules);
        updatedZones.push({
            ...zone,
            triggerRules: nextRules.length > 0 ? {
                logic: RulesLogic.AND,
                rules: nextRules
            } : undefined
        });
    });

    const knownIds = new Set(updatedZones.map((z) => z.id));
    const remaining = ambientZones.filter((z) => !knownIds.has(z.id));
    return {updatedZones: [...updatedZones, ...remaining], spotManagedRules};
};
